import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { TrendingUp, Users, DollarSign, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const SystemAdminAnalyticsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [revenueTrends, setRevenueTrends] = useState<any>(null);
  const [trialConversion, setTrialConversion] = useState<any>(null);
  const [churnAnalysis, setChurnAnalysis] = useState<any>(null);
  const [featureAdoption, setFeatureAdoption] = useState<any>(null);
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadRevenueTrends(),
        loadTrialConversion(),
        loadChurnAnalysis(),
        loadFeatureAdoption()
      ]);
    } catch (error: any) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalytics();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  const loadRevenueTrends = async () => {
    try {
      const response = await apiService.getRevenueTrends(period);
      if (response.success) {
        setRevenueTrends(response.data);
      }
    } catch (error: any) {
      console.error('Error loading revenue trends:', error);
    }
  };

  const loadTrialConversion = async () => {
    try {
      const response = await apiService.getTrialConversionFunnel();
      if (response.success) {
        setTrialConversion(response.data);
      }
    } catch (error: any) {
      console.error('Error loading trial conversion:', error);
    }
  };

  const loadChurnAnalysis = async () => {
    try {
      const response = await apiService.getChurnAnalysis();
      if (response.success) {
        setChurnAnalysis(response.data);
      }
    } catch (error: any) {
      console.error('Error loading churn analysis:', error);
    }
  };

  const loadFeatureAdoption = async () => {
    try {
      const response = await apiService.getFeatureAdoption();
      if (response.success) {
        setFeatureAdoption(response.data);
      }
    } catch (error: any) {
      console.error('Error loading feature adoption:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Analytics Dashboard</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Comprehensive insights into your SaaS metrics</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as 'daily' | 'weekly' | 'monthly')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Revenue Trends */}
        {revenueTrends && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Revenue Trends
              </h2>
              <div className="flex items-center gap-4 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Total: </span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    ${revenueTrends.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Growth: </span>
                  <span className={`font-semibold ${revenueTrends.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {revenueTrends.growthRate >= 0 ? '+' : ''}{revenueTrends.growthRate.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueTrends.dataPoints}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  formatter={(value: number) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  name="Revenue"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Trial Conversion Funnel */}
        {trialConversion && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-4">
                <Users className="w-5 h-5" />
                Trial Conversion Funnel
              </h2>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Conversion Rate</span>
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {trialConversion.conversionRate.toFixed(1)}%
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Total Trials: </span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{trialConversion.totalTrials}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Converted: </span>
                    <span className="font-semibold text-green-600">{trialConversion.convertedToPaid}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Expired: </span>
                    <span className="font-semibold text-red-600">{trialConversion.expired}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Active: </span>
                    <span className="font-semibold text-blue-600">{trialConversion.active}</span>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={trialConversion.funnelSteps}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stepName" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5" />
                Conversions by Plan
              </h2>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={Object.entries(trialConversion.conversionsByPlan).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(trialConversion.conversionsByPlan).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Churn Analysis */}
        {churnAnalysis && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5" />
              Churn Analysis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <div className="text-sm text-red-600 dark:text-red-400 mb-1">Churn Rate</div>
                <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                  {churnAnalysis.churnRate.toFixed(2)}%
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Churned (30d)</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {churnAnalysis.churnedLast30Days}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Churned (90d)</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {churnAnalysis.churnedLast90Days}
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <div className="text-sm text-red-600 dark:text-red-400 mb-1">Revenue Lost</div>
                <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                  ${churnAnalysis.revenueLost.toLocaleString()}
                </div>
              </div>
            </div>
            {churnAnalysis.churnHistory && churnAnalysis.churnHistory.length > 0 && (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={churnAnalysis.churnHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="churned" stroke="#EF4444" name="Churned" />
                  <Line type="monotone" dataKey="churnRate" stroke="#F59E0B" name="Churn Rate %" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        )}

        {/* Feature Adoption */}
        {featureAdoption && featureAdoption.featureStats && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Feature Adoption</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(featureAdoption.featureStats).map(([featureName, stats]: [string, any]) => (
                <div key={featureName} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{featureName}</div>
                  <div className="text-2xl font-bold text-blue-600 mb-1">{stats.adoptionRate.toFixed(1)}%</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {stats.totalEnabled} of {stats.totalCompanies} companies
                  </div>
                  {stats.enabledLast30Days > 0 && (
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                      +{stats.enabledLast30Days} in last 30 days
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemAdminAnalyticsPage;

